import { useEffect, useMemo, useRef } from 'react';

interface ScrollToHashOptions {
  hash: string;
  offset: number;
}

const normalizeHash = (h: string) => (h.startsWith('#') ? h : `#${h}`);

/** Default offset; section wrappers should use matching `id` (hash without `#`) for in-page links. */
export const HASH_SECTION_SCROLL_OFFSET = 100;

export function scrollToHashSection(
  hash: string,
  offset: number = HASH_SECTION_SCROLL_OFFSET
): void {
  const normalizedHash = normalizeHash(hash);
  const id = normalizedHash.slice(1);
  const el = document.getElementById(id);
  if (!el) return;
  const scrollTopDistance = el.getBoundingClientRect().top + window.scrollY;
  window.scrollTo({
    top: scrollTopDistance - offset,
    behavior: 'smooth',
  });
}

const useScrollToHash = ({ hash, offset }: ScrollToHashOptions) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const normalizedHash = useMemo(() => normalizeHash(hash), [hash]);

  useEffect(() => {
    const scrollIfMatch = () => {
      if (window.location.hash !== normalizedHash) return;
      scrollToHashSection(normalizedHash, offset);
    };

    scrollIfMatch();
    window.addEventListener('hashchange', scrollIfMatch);
    return () => window.removeEventListener('hashchange', scrollIfMatch);
  }, [normalizedHash, offset]);

  return elementRef;
};

export default useScrollToHash;
